/**
 * 課題文LLM生成前の健康心理条件選択ダイアログ
 * DBの実データを表示し、ユーザーが行（カテゴリ）をチェックボックスで選択
 * + 行ごとにLvを個別トグルで選択可能
 */

import { useState, useCallback, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  conditionDataToCellGrid,
  selectedCellsToSummary,
  type DisplayCell,
  type CellGrid,
} from '../../lib/conditionDataToCells';
import { FileCheck, Loader2 } from 'lucide-react';

/** 条件オブジェクト（API から取得） */
export interface ConditionRecord {
  id: number;
  condition_data?: Record<string, unknown>;
  gender?: string;
  age_range?: string;
}

export type GeneratePayload = {
  conditionId: number;
  selectedCells: DisplayCell[];
  lvSelections?: Record<string, number[]>; // e.g. { "C1": [1,2,3], "C3": [1,2,3,4,5,6] }
};

interface InstructionGenerateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conditions: ConditionRecord[];
  currentConditionId: number | null;
  onConfirm: (payload: GeneratePayload) => void;
  isGenerating?: boolean;
}

/** gridのヘッダーからLv列のインデックスを取得 */
function getLvColIndices(headers: string[]): number[] {
  return headers
    .map((h, i) => (h.startsWith('Lv') ? i : -1))
    .filter((i) => i >= 0);
}

/** gridの行からカテゴリIDを取得（ID列＝最初のセル） */
function getRowCategoryId(row: { cells: DisplayCell[] }): string {
  const idCell = row.cells.find((c) => c.header === 'ID');
  return idCell?.value ?? '';
}

export function InstructionGenerateDialog({
  open,
  onOpenChange,
  conditions,
  currentConditionId,
  onConfirm,
  isGenerating = false,
}: InstructionGenerateDialogProps) {
  const [selectedConditionId, setSelectedConditionId] = useState<string>('');
  const [selectedRowIndices, setSelectedRowIndices] = useState<Set<number>>(new Set());
  // カテゴリID → 選択されたLv番号のSet
  const [lvSelections, setLvSelections] = useState<Map<string, Set<number>>>(new Map());

  const condition = conditions.find((c) => c.id === parseInt(selectedConditionId || '0', 10)) ?? null;
  const grid: CellGrid | null = condition?.condition_data
    ? conditionDataToCellGrid(condition.condition_data)
    : null;

  // gridのヘッダーにLv列があるか（categoryTable形式のみ）
  const hasLvColumns = grid ? grid.headers.some((h) => h.startsWith('Lv')) : false;
  const lvColIndices = grid ? getLvColIndices(grid.headers) : [];
  const maxLv = lvColIndices.length; // 通常6

  useEffect(() => {
    if (open) {
      setSelectedConditionId(currentConditionId ? String(currentConditionId) : '');
      setSelectedRowIndices(new Set());
      setLvSelections(new Map());
    }
  }, [open, currentConditionId]);

  const handleRowToggle = useCallback((rowIndex: number, categoryId: string) => {
    setSelectedRowIndices((prev: Set<number>) => {
      const next = new Set(prev);
      if (next.has(rowIndex)) {
        next.delete(rowIndex);
      } else {
        next.add(rowIndex);
      }
      return next;
    });
    // 初回選択時にデフォルトで全Lv選択
    setLvSelections((prev) => {
      const next = new Map(prev);
      if (!next.has(categoryId)) {
        const allLvs = new Set<number>();
        for (let i = 1; i <= (maxLv || 6); i++) allLvs.add(i);
        next.set(categoryId, allLvs);
      }
      return next;
    });
  }, [maxLv]);

  const handleLvToggle = useCallback((categoryId: string, lvNum: number) => {
    setLvSelections((prev) => {
      const next = new Map(prev);
      const current = next.get(categoryId) ?? new Set<number>();
      const updated = new Set(current);
      if (updated.has(lvNum)) {
        updated.delete(lvNum);
      } else {
        updated.add(lvNum);
      }
      next.set(categoryId, updated);
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (!grid) return;
    setSelectedRowIndices(new Set(grid.rows.map((r) => r.rowIndex)));
    setLvSelections((prev) => {
      const next = new Map(prev);
      for (const row of grid.rows) {
        const catId = getRowCategoryId(row);
        if (catId && !next.has(catId)) {
          const allLvs = new Set<number>();
          for (let i = 1; i <= (maxLv || 6); i++) allLvs.add(i);
          next.set(catId, allLvs);
        }
      }
      return next;
    });
  }, [grid, maxLv]);

  const handleClearSelection = useCallback(() => {
    setSelectedRowIndices(new Set());
    setLvSelections(new Map());
  }, []);

  // 選択されたセルを取得（Lvトグルでフィルタ）
  const getFilteredSelectedCells = (): DisplayCell[] => {
    if (!grid) return [];
    const cells: DisplayCell[] = [];
    for (const row of grid.rows) {
      if (!selectedRowIndices.has(row.rowIndex)) continue;
      const catId = getRowCategoryId(row);
      const selectedLvs = lvSelections.get(catId);
      for (const cell of row.cells) {
        if (cell.header.startsWith('Lv') && selectedLvs) {
          const lvNum = parseInt(cell.header.replace('Lv', ''), 10);
          if (!selectedLvs.has(lvNum)) continue;
        }
        cells.push(cell);
      }
    }
    return cells;
  };

  const handleConfirm = () => {
    const id = selectedConditionId ? parseInt(selectedConditionId, 10) : 0;
    if (!id || !grid) return;

    const selectedCells = getFilteredSelectedCells();

    const lvSelectionsObj: Record<string, number[]> = {};
    if (hasLvColumns) {
      for (const row of grid.rows) {
        if (!selectedRowIndices.has(row.rowIndex)) continue;
        const catId = getRowCategoryId(row);
        if (catId) {
          const lvs = lvSelections.get(catId);
          lvSelectionsObj[catId] = lvs ? Array.from(lvs).sort() : Array.from({ length: maxLv || 6 }, (_, i) => i + 1);
        }
      }
    }

    onConfirm({
      conditionId: id,
      selectedCells,
      lvSelections: hasLvColumns ? lvSelectionsObj : undefined,
    });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next && !isGenerating) {
      onOpenChange(false);
    } else if (next) {
      onOpenChange(true);
    }
  };

  const selectedCells = getFilteredSelectedCells();
  const canConfirm = !!selectedConditionId && selectedCells.length > 0;
  const selectionSummary = grid ? selectedCellsToSummary(selectedCells, grid) : '';

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto flex flex-col">
        <DialogHeader>
          <DialogTitle>課題文に適用する健康心理条件を選択</DialogTitle>
          <DialogDescription>
            条件を選択し、課題文に反映させる行とLvを指定してください。
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 flex-1 min-h-0 overflow-hidden flex flex-col">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">健康心理条件:</span>
            <Select
              value={selectedConditionId}
              onValueChange={(v: string) => {
                setSelectedConditionId(v);
                setSelectedRowIndices(new Set());
                setLvSelections(new Map());
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="条件を選択してください" />
              </SelectTrigger>
              <SelectContent>
                {conditions.map((c) => (
                  <SelectItem key={c.id} value={String(c.id)}>
                    ID: {c.id} ({c.gender ?? '-'} / {c.age_range ?? '-'})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {conditions.length === 0 && (
            <p className="text-sm text-amber-600">
              健康心理条件が登録されていません。先に「健康心理条件管理」で条件を登録してください。
            </p>
          )}

          {condition && !grid && (
            <p className="text-sm text-muted-foreground">
              この条件に表示可能なデータがありません。
            </p>
          )}

          {grid && (
            <>
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">
                  行をチェックで選択し、Lvをトグルで指定してください。
                </span>
                <div className="flex gap-2">
                  <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                    全選択
                  </Button>
                  <Button variant="ghost" size="sm" onClick={handleClearSelection}>
                    選択解除
                  </Button>
                </div>
              </div>

              <div className="overflow-auto border rounded-md flex-1 min-h-[200px]">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr>
                      <th className="border border-border p-2 bg-muted/50 sticky top-0 w-10"></th>
                      {grid.headers.map((h, i) => (
                        <th
                          key={i}
                          className="border border-border p-2 text-left bg-muted/50 sticky top-0 whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                      {hasLvColumns && (
                        <th className="border border-border p-2 text-left bg-muted/50 sticky top-0 whitespace-nowrap">
                          Lv選択
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody>
                    {grid.rows.map((row) => {
                      const isSelected = selectedRowIndices.has(row.rowIndex);
                      const catId = getRowCategoryId(row);
                      const selectedLvs = lvSelections.get(catId) ?? new Set<number>();
                      return (
                        <tr
                          key={row.rowIndex}
                          className={isSelected ? 'bg-blue-50 dark:bg-blue-900/20' : 'hover:bg-muted/30'}
                        >
                          <td className="border border-border p-2 text-center">
                            <Checkbox
                              checked={isSelected}
                              onCheckedChange={() => handleRowToggle(row.rowIndex, catId)}
                            />
                          </td>
                          {row.cells.map((cell) => {
                            const isLvCell = cell.header.startsWith('Lv');
                            let dimmed = false;
                            if (isSelected && isLvCell) {
                              const lvNum = parseInt(cell.header.replace('Lv', ''), 10);
                              dimmed = !selectedLvs.has(lvNum);
                            }
                            return (
                              <td
                                key={`${cell.rowIndex}-${cell.colIndex}`}
                                className={`border border-border p-2 text-xs max-w-[200px] ${dimmed ? 'opacity-25' : ''}`}
                              >
                                <span className="line-clamp-3">{cell.value || '—'}</span>
                              </td>
                            );
                          })}
                          {hasLvColumns && (
                            <td className="border border-border p-2 whitespace-nowrap">
                              {isSelected ? (
                                <div className="flex flex-wrap gap-1">
                                  {Array.from({ length: maxLv || 6 }, (_, i) => i + 1).map((n) => (
                                    <button
                                      key={n}
                                      type="button"
                                      onClick={() => handleLvToggle(catId, n)}
                                      className={`px-1.5 py-0.5 text-xs rounded border transition-colors ${
                                        selectedLvs.has(n)
                                          ? 'bg-blue-500 text-white border-blue-500'
                                          : 'bg-white text-gray-500 border-gray-300 hover:border-blue-300'
                                      }`}
                                    >
                                      {n}
                                    </button>
                                  ))}
                                </div>
                              ) : (
                                <span className="text-xs text-muted-foreground">—</span>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {selectedCells.length > 0 && (
                <div className="rounded-md bg-muted/50 p-3 text-sm">
                  <div className="font-medium mb-2">
                    選択中（{selectedRowIndices.size}行 / {selectedCells.length}セル）:
                  </div>
                  <pre className="text-xs text-muted-foreground whitespace-pre-wrap max-h-24 overflow-y-auto">
                    {selectionSummary}
                  </pre>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)} disabled={isGenerating}>
            キャンセル
          </Button>
          <Button onClick={handleConfirm} disabled={!canConfirm || isGenerating}>
            {isGenerating ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <FileCheck className="w-4 h-4 mr-2" />
            )}
            課題文を生成
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
